import { getCachedClientConfig } from "@caixa/lib/clientConfig";
import { imprimirCupom } from "@/lib/cupom";
import { formatBRL } from "@caixa/lib/format";
import type { RelatorioDiaCompleto } from "./types";

function fmtQtd(q: number): string {
  return q.toFixed(3).replace(/\.?0+$/, "");
}

function fmtDataBR(iso: string): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function volumePico(report: RelatorioDiaCompleto): number {
  let max = 0;
  for (const v of report.vendas_por_hora) {
    if (v.quantidade > max) max = v.quantidade;
  }
  return max;
}

/** HTML do relatório para impressão (mesmo estilo 80mm do cupom não fiscal). */
export function buildRelatorioPrintHtml(
  report: RelatorioDiaCompleto,
  empresaNome: string
): string {
  const sep = "--------------------------------";
  const linhas: string[] = [
    empresaNome.toUpperCase(),
    sep,
    "RELATORIO DIARIO",
    `Data: ${fmtDataBR(report.date)}`,
    sep,
    "RESUMO FINANCEIRO",
    `Faturamento...... ${formatBRL(report.faturamento_total)}`,
    `Credito.......... ${formatBRL(report.pagamentos.credito)}`,
    `Debito........... ${formatBRL(report.pagamentos.debito)}`,
    `Pix.............. ${formatBRL(report.pagamentos.pix)}`,
    `Dinheiro......... ${formatBRL(report.pagamentos.dinheiro)}`,
    `Valor em caixa... ${formatBRL(report.valor_em_caixa)}`,
    sep,
    "TOP 5 PRODUTOS (QTD / TOTAL)",
  ];

  if (report.top_produtos.length === 0) {
    linhas.push("Nenhuma venda de produto", "registrada neste dia.");
  } else {
    for (const p of report.top_produtos) {
      linhas.push(`${fmtQtd(p.quantidade)} x ${formatBRL(p.valor_total)}`);
      linhas.push(p.nome || "(sem nome)");
    }
  }

  linhas.push(sep, "HORARIO(S) PICO (VOLUME)");

  const maxV = volumePico(report);
  const picos = report.horarios_pico;
  if (maxV < 0.00001 || picos.length === 0) {
    linhas.push("Sem dados de volume por hora.");
  } else {
    for (const h of picos.sort((a, b) => a - b)) {
      linhas.push(`${String(h).padStart(2, "0")}h  volume ${fmtQtd(maxV)} unid.`);
    }
  }

  const clientName = getCachedClientConfig().fantasy_name || getCachedClientConfig().name;
  linhas.push(sep, "Relatório gerado pelo sistema", `GrandSystem / ${clientName}`, "====================");

  const corpo = linhas.join("\n");

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Relatório caixa ${report.date}</title>
<style>
  @page { size: 80mm auto; margin: 0; }
  html, body { width: 80mm; margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    font-family: "Courier New", Courier, monospace;
    font-size: 12px;
    line-height: 1.15;
    font-weight: 700;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .wrap { width: 80mm; display: flex; justify-content: center; }
  .cupom { width: 72mm; padding: 2mm 0; }
  pre { margin: 0; white-space: pre; font: inherit; color: #000 !important; }
  .line { border-top: 1px dashed #000; margin: 4px 0; }
  * { color: #000 !important; background: transparent !important; }
</style>
</head>
<body>
  <div class="wrap">
    <div class="cupom">
      <div class="line"></div>
      <pre>${corpo
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")}</pre>
    </div>
  </div>
</body>
</html>`;
}

export async function imprimirRelatorioDia(
  report: RelatorioDiaCompleto,
  empresaNome: string
): Promise<void> {
  const html = buildRelatorioPrintHtml(report, empresaNome);
  await imprimirCupom(html);
}
